import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import JobPostingSuccess from '../Popups/JobPostingSuccess';
import BroadcastDialog from '../Broadcast/BroadcastDialog';
import { getApiUrl } from '../../../CompanyAdmin/utils/api';
import DropdownWithOther from '../../../CompanyAdmin/components/DropdownWithOther';
import {
  buildDropdownFieldsFromValues,
  CLIENT_NOTICE_PERIOD_OPTIONS,
  DEMAND_LEVEL_OPTIONS,
  DEPARTMENT_OPTIONS,
  HOLD_CLOSED_REASON_OPTIONS,
  MAJOR_SYSTEM_OPTIONS,
  resolveDropdownFields,
} from '../../../CompanyAdmin/utils/excelFieldOptions';
import {
  formatSalaryDisplay,
  normalizeSalaryForSave,
  validateSalaryRangeInput,
} from '../../../CompanyAdmin/utils/salaryUtils';
import { Button } from '@/Components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { Input } from '@/Components/ui/input';
import { Textarea } from '@/Components/ui/textarea';
import { Label } from '@/Components/ui/label';
import { Checkbox } from '@/Components/ui/checkbox';
import { Badge } from '@/Components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { FormDescription } from '@/Components/ui/form-description';
import { DatePicker } from '@/Components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import { getColor } from '@/config/theme';
import { StepIndicator } from './StepIndicator';
import { ReviewStep } from './ReviewStep';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export const PostJobForm = ({
  mode = 'create', // 'create' | 'edit'
  embedded = false,
  initialData = null, // when editing existing job/JD
  jobId: explicitJobId = null,
  onCancel,
  onSuccess,
} = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [overrideCompanyEmail, setOverrideCompanyEmail] = useState(null);
  const [formData, setFormDataState] = useState({
    jobTitle: '',
    jobType: '',
    companyLocation: '',
    minExperience: '',
    maxExperience: '',
    employmentType: '',
    salaryRange: '',
    preferredQualification: '',
    jobSummary: '',
    keyResponsibilities: '',
    requiredSkills: '',
    optionalSkills: '',
    softSkills: '',
    criticalSkills: [],
    workHours: '',
    companyCulture: '',
    travelRequirement: '',
    hiringProcess: '',
    applicationDeadline: '',
    mandatoryProfile: false,
    requireVideoResume: false,
    // College preference {mode, tiers, schools} — mode 'any'|'preferred'|'required'
    collegeRequirement: { mode: 'any', tiers: [], schools: [] },
    preferrenceOne: '',
    preferrenceTwo: '',
    preferrenceThree: '',
    requirementStatus: 'active',
    department: '',
    openPositions: '1',
    clientNoticePeriod: '',
    customerFeedback: '',
    demandLevel: '',
    candidateCallScript: '',
    holdOrClosedReason: '',
    spoc: '',
    requirementDate: '',
    budgetAssignedByClient: '',
    majorSystemExpertise: '',
    questionsToAsk: '',
    targetClosureDate: '',
  });

  // Wrapper to track setFormData calls
  const setFormData = (newData) => {
    // Debug log/trace removed — was leaking form data and stack on every state update.
    setFormDataState(newData);
  };

  const [showSuccess, setShowSuccess] = useState(false);
  // The job just created, kept so the success popup can hand off to the
  // Broadcast dialog (publish + list + share) for that job.
  const [postedJobId, setPostedJobId] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  const [newCriticalSkill, setNewCriticalSkill] = useState('');
  const [collegeSchoolInput, setCollegeSchoolInput] = useState('');
  const addCollegeSchool = () => {
    const v = collegeSchoolInput.trim();
    if (!v) return;
    setFormData(prev => {
      const cur = prev.collegeRequirement || { mode: 'any', tiers: [], schools: [] };
      if ((cur.schools || []).some(s => s.toLowerCase() === v.toLowerCase())) return prev;
      return { ...prev, collegeRequirement: { ...cur, schools: [...(cur.schools || []), v] } };
    });
    setCollegeSchoolInput('');
  };
  const [showPreview, setShowPreview] = useState(false);
  
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrors, setStepErrors] = useState({});
  
  // Step configuration
  const steps = [
    { title: 'Basic Info', description: 'Job details' },
    { title: 'Job Details', description: 'Summary & responsibilities' },
    { title: 'Skills', description: 'Required & optional skills' },
    { title: 'Company', description: 'Culture & work details' },
    { title: 'Preferences', description: 'Additional preferences' },
    { title: 'Review', description: 'Review & submit' }
  ];
  
  const totalSteps = steps.length;
  
  // Track if data has been loaded to prevent multiple loads
  const dataLoadedRef = useRef(false);
  
  // Track if submit was explicitly triggered by button click
  const submitButtonClickedRef = useRef(false);
  
  // Protected setFormData function that prevents resets after AI data is loaded
  const protectedSetFormData = (newData) => {
    if (dataLoadedRef.current && isAiGenerated) {
      // Debug log removed — was noisy on every guarded form-reset attempt.
      return;
    }
    setFormData(newData);
  };
  
  // Skill tagging states
  const [requiredSkillTags, setRequiredSkillTags] = useState([]);
  const [optionalSkillTags, setOptionalSkillTags] = useState([]);
  const [softSkillTags, setSoftSkillTags] = useState([]);
  const [skillInput, setSkillInput] = useState({ required: '', optional: '', soft: '', critical: '' });
  const [skillSuggestions, setSkillSuggestions] = useState({ required: [], optional: [], soft: [], critical: [] });
  const [showSuggestions, setShowSuggestions] = useState({ required: false, optional: false, soft: false, critical: false });
  const [jdDropdownFields, setJdDropdownFields] = useState({
    department: { select: '', other: '' },
    demandLevel: { select: '', other: '' },
    clientNoticePeriod: { select: '', other: '' },
    holdOrClosedReason: { select: '', other: '' },
    majorSystemExpertise: { select: '', other: '' },
  });

  const JD_DROPDOWN_KEYS = [
    'department',
    'demandLevel',
    'clientNoticePeriod',
    'holdOrClosedReason',
    'majorSystemExpertise',
  ];

  const updateJdDropdownField = (key, patch) => {
    setJdDropdownFields((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  };

  // Enhanced skills database with categories and related skills
  const skillsDatabase = {
    'JavaScript': {
      related: ['TypeScript', 'React', 'Vue.js', 'Angular', 'Node.js', 'jQuery', 'ES6', 'Webpack'],
      category: 'Frontend'
    },
    'Python': {
      related: ['Django', 'Flask', 'Pandas', 'NumPy', 'Machine Learning', 'Data Science', 'FastAPI'],
      category: 'Backend'
    },
    'React': {
      related: ['JavaScript', 'TypeScript', 'Redux', 'Next.js', 'JSX', 'Hooks', 'Context API'],
      category: 'Frontend'
    },
    'Java': {
      related: ['Spring Boot', 'Maven', 'Hibernate', 'JUnit', 'Microservices', 'JVM', 'Gradle'],
      category: 'Backend'
    },
    'AWS': {
      related: ['EC2', 'S3', 'Lambda', 'RDS', 'CloudFormation', 'Docker', 'Kubernetes'],
      category: 'Cloud'
    },
    'SQL': {
      related: ['MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Database Design', 'Query Optimization'],
      category: 'Database'
    },
    'Docker': {
      related: ['Kubernetes', 'Containerization', 'DevOps', 'CI/CD', 'Microservices', 'AWS'],
      category: 'DevOps'
    },
    'Leadership': {
      related: ['Management', 'Team Building', 'Strategic Planning', 'Communication', 'Decision Making'],
      category: 'Soft Skills'
    },
    'Communication': {
      related: ['Presentation', 'Writing', 'Public Speaking', 'Teamwork', 'Collaboration'],
      category: 'Soft Skills'
    },
    'Problem Solving': {
      related: ['Critical Thinking', 'Analytical Skills', 'Debugging', 'Troubleshooting', 'Innovation'],
      category: 'Soft Skills'
    }
  };

  // Get skill suggestions based on input
  const getSkillSuggestions = (input, currentSkills, type) => {
    if (!input || input.length < 2) return [];
    
    const suggestions = [];
    const inputLower = input.toLowerCase();
    
    // Search in skills database
    Object.keys(skillsDatabase).forEach(skill => {
      if (skill.toLowerCase().includes(inputLower) && 
          !currentSkills.includes(skill) && 
          !suggestions.includes(skill)) {
        suggestions.push(skill);
      }
    });
    
    // Search in related skills
    Object.values(skillsDatabase).forEach(skillData => {
      skillData.related.forEach(relatedSkill => {
        if (relatedSkill.toLowerCase().includes(inputLower) && 
            !currentSkills.includes(relatedSkill) && 
            !suggestions.includes(relatedSkill)) {
          suggestions.push(relatedSkill);
        }
      });
    });
    
    // Add category-based suggestions
    if (type === 'required') {
      suggestions.push(...['Git', 'Agile', 'Scrum', 'Testing', 'Code Review']);
    } else if (type === 'soft') {
      suggestions.push(...['Adaptability', 'Time Management', 'Creativity', 'Empathy']);
    }
    
    return suggestions.slice(0, 8); // Limit to 8 suggestions
  };

  // Rich text editor modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ]
  };

  // Character limits
  const charLimits = {
    jobSummary: 500,
    keyResponsibilities: 1000,
    preferredQualification: 500,
    companyCulture: 500,
    hiringProcess: 500
  };

  // Strip HTML tags for character count
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Get character count
  const getCharCount = (fieldName) => {
    const text = stripHtml(formData[fieldName] || '');
    return text.length;
  };

  // Step validation function
  const validateStep = (stepNumber) => {
    const errors = {};
    
    switch (stepNumber) {
      case 0: // Basic Information
        if (!formData.jobTitle?.trim()) {
          errors.jobTitle = 'Job title is required';
        }
        if (!formData.jobType) {
          errors.jobType = 'Job type is required';
        }
        if (!formData.companyLocation?.trim()) {
          errors.companyLocation = 'Company location is required';
        }
        const minExpNum = Number(formData.minExperience);
        const maxExpNum = Number(formData.maxExperience);

        if (!formData.minExperience || Number.isNaN(minExpNum) || minExpNum < 0) {
          errors.minExperience = 'Minimum experience is required and must be 0 or greater';
        }
        if (
          formData.maxExperience !== '' &&
          !Number.isNaN(maxExpNum) &&
          !Number.isNaN(minExpNum) &&
          maxExpNum < minExpNum
        ) {
          errors.maxExperience = 'Maximum experience must be greater than or equal to minimum experience';
        }
        Object.assign(errors, validateSalaryRangeInput(formData.salaryRange));
        break;
        
      case 1: // Job Details
        if (!stripHtml(formData.jobSummary || '').trim()) {
          errors.jobSummary = 'Job summary is required';
        }
        if (!stripHtml(formData.keyResponsibilities || '').trim()) {
          errors.keyResponsibilities = 'Key responsibilities are required';
        }
        break;
        
      case 2: // Skills & Qualifications
        if (requiredSkillTags.length === 0 && !formData.requiredSkills?.trim()) {
          errors.requiredSkills = 'At least one required skill is required';
        }
        break;
        
      case 3: // Company & Work Details (optional, no validation)
      case 4: // Preferences (optional, no validation)
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  // Navigation functions
  const handleNext = () => {
    const errors = validateStep(currentStep);
    
    if (Object.keys(errors).length > 0) {
      setStepErrors({ [currentStep]: errors });
      return;
    }
    
    setStepErrors({});
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setStepErrors({});
    }
  };

  const handleStepClick = (stepIndex) => {
    // Only allow clicking on completed steps or current step
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
      setStepErrors({});
    }
  };

  const handleEditStep = (stepIndex) => {
    setCurrentStep(stepIndex);
    setStepErrors({});
  };

  // Check for pre-filled data from AI components
  useEffect(() => {
    // If coming from Company Admin with a selected company, fetch its email to post on its behalf
    try {
      const params = new URLSearchParams(location.search || '');
      const companyID = params.get('companyID');
      if (companyID) {
        (async () => {
          try {
            const res = await fetch(getApiUrl(`/api/get_company_by_ID?companyID=${encodeURIComponent(companyID)}`), {
              credentials: 'include'
            });
            if (res.ok) {
              const data = await res.json();
              const email = data?.email || data?.company_email || data?.companyEmail || null;
              if (email) setOverrideCompanyEmail(email);
            }
          } catch (err) {
            console.error('Failed to fetch company for posting context:', err);
          }
        })();
      }
    } catch (e) {
      console.error('Error initializing admin posting context:', e);
    }

    // 0) If used as an embedded editor, prefer `initialData`
    if (initialData && !dataLoadedRef.current) {
      // Debug log removed — was leaking job form initial data to console.

      const ensureString = (value) => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object' && value !== null) return JSON.stringify(value);
        return String(value || '');
      };

      setFormData(prevData => {
        const nextData = {
        ...prevData,
        jobTitle: ensureString(initialData.jobTitle || initialData.job_title) || prevData.jobTitle,
        jobType: initialData.jobType || initialData.job_type || prevData.jobType,
        companyLocation: ensureString(initialData.companyLocation || initialData.location) || prevData.companyLocation,
        minExperience:
          initialData.minExperience ||
          initialData.min_experience ||
          initialData.min_exp ||
          (initialData.experienceRange || initialData.experience_range)?.split('-')[0]?.trim() ||
          prevData.minExperience,
        maxExperience:
          initialData.maxExperience ||
          initialData.max_experience ||
          initialData.max_exp ||
          (initialData.experienceRange || initialData.experience_range)?.split('-')[1]?.trim() ||
          prevData.maxExperience,
        employmentType: initialData.employmentType || initialData.employment_type || prevData.employmentType,
        salaryRange: (() => {
          const raw = ensureString(initialData.salaryRange || initialData.salary_range);
          if (!raw) return prevData.salaryRange;
          // Keep valid text; clear legacy negative/invalid values in the form.
          const errs = validateSalaryRangeInput(raw);
          return errs.salaryRange ? '' : raw;
        })(),
        preferredQualification:
          ensureString(
            initialData.preferredQualification ||
              initialData.preferred_qualification ||
              initialData.preferred_qualifications_raw ||
              initialData.preferred_qualifications
          ) || prevData.preferredQualification,
        jobSummary:
          ensureString(initialData.jobSummary || initialData.job_summary || initialData.job_overview) ||
          prevData.jobSummary,
        keyResponsibilities:
          ensureString(initialData.keyResponsibilities || initialData.key_responsibilities) ||
          prevData.keyResponsibilities,
        requiredSkills:
          ensureString(initialData.requiredSkills || initialData.required_skills || initialData.required_qualifications) ||
          prevData.requiredSkills,
        optionalSkills:
          ensureString(initialData.optionalSkills || initialData.optional_skills || initialData.good_to_have_skills) ||
          prevData.optionalSkills,
        softSkills: ensureString(initialData.softSkills || initialData.soft_skills) || prevData.softSkills,
        criticalSkills: (() => {
          const skills = initialData.criticalSkills || initialData.critical_skills;
          if (Array.isArray(skills)) return skills.filter(Boolean).map(String);
          if (typeof skills === 'string') {
            if (skills.startsWith('[') && skills.endsWith(']')) {
              try {
                const parsed = JSON.parse(skills);
                if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
              } catch (e) {
                // fall through
              }
            }
            return skills
              .split(',')
              .map(s => s.trim().replace(/["\[\]]/g, ''))
              .filter(Boolean);
          }
          return prevData.criticalSkills || [];
        })(),
        workHours: initialData.workHours || initialData.work_hours || prevData.workHours,
        companyCulture:
          ensureString(initialData.companyCulture || initialData.company_culture || initialData.company_overview) ||
          prevData.companyCulture,
        travelRequirement: initialData.travelRequirement || initialData.travel_requirement || prevData.travelRequirement,
        hiringProcess:
          ensureString(initialData.hiringProcess || initialData.hiring_process || initialData.how_to_apply) ||
          prevData.hiringProcess,
        applicationDeadline:
          (initialData.applicationDeadline || initialData.application_deadline || prevData.applicationDeadline) ?? '',
        mandatoryProfile: Boolean(initialData.mandatoryProfile || initialData.mandatory_profile || prevData.mandatoryProfile),
        requireVideoResume: Boolean(initialData.requireVideoResume || initialData.require_video_resume || prevData.requireVideoResume),
        collegeRequirement: (() => {
          const r = initialData.collegeRequirement || initialData.college_requirement;
          if (r && typeof r === 'object' && !Array.isArray(r)) {
            return {
              mode: ['any', 'preferred', 'required'].includes(r.mode) ? r.mode : 'any',
              tiers: Array.isArray(r.tiers) ? r.tiers.filter(t => t === 1 || t === 2) : [],
              schools: Array.isArray(r.schools) ? r.schools : [],
            };
          }
          return prevData.collegeRequirement || { mode: 'any', tiers: [], schools: [] };
        })(),
        preferrenceOne: ensureString(initialData.preferrenceOne || initialData.preferrence_one) || prevData.preferrenceOne,
        preferrenceTwo: ensureString(initialData.preferrenceTwo || initialData.preferrence_two) || prevData.preferrenceTwo,
        preferrenceThree:
          ensureString(initialData.preferrenceThree || initialData.preferrence_three) || prevData.preferrenceThree,
        requirementStatus: initialData.requirementStatus || initialData.requirement_status || prevData.requirementStatus || 'active',
        department: ensureString(initialData.department) || prevData.department,
        openPositions: (() => {
          const raw = initialData.openPositions ?? initialData.open_positions ?? prevData.openPositions;
          if (raw === undefined || raw === null || raw === '' || Number(raw) < 1) return '1';
          return String(raw);
        })(),
        clientNoticePeriod: ensureString(initialData.clientNoticePeriod || initialData.client_notice_period) || prevData.clientNoticePeriod,
        customerFeedback: ensureString(initialData.customerFeedback || initialData.customer_feedback) || prevData.customerFeedback,
        demandLevel: ensureString(initialData.demandLevel || initialData.demand_level) || prevData.demandLevel,
        candidateCallScript: ensureString(initialData.candidateCallScript || initialData.candidate_call_script) || prevData.candidateCallScript,
        holdOrClosedReason: ensureString(initialData.holdOrClosedReason || initialData.hold_or_closed_reason) || prevData.holdOrClosedReason,
        spoc: (() => {
          const direct = ensureString(initialData.spoc || '').trim();
          if (direct) return direct;
          const parts = [
            ensureString(initialData.spocName || initialData.spoc_name || ''),
            ensureString(initialData.spocEmail || initialData.spoc_email || ''),
            ensureString(initialData.spocPhone || initialData.spoc_phone || ''),
          ].map((value) => value.trim()).filter(Boolean);
          return parts.length ? parts.join('\n') : (prevData.spoc || '');
        })(),
        requirementDate: initialData.requirementDate || initialData.requirement_date || prevData.requirementDate || '',
        budgetAssignedByClient: ensureString(initialData.budgetAssignedByClient || initialData.budget_assigned_by_client) || prevData.budgetAssignedByClient,
        majorSystemExpertise: ensureString(initialData.majorSystemExpertise || initialData.major_system_expertise) || prevData.majorSystemExpertise,
        questionsToAsk: ensureString(initialData.questionsToAsk || initialData.questions_to_ask) || prevData.questionsToAsk,
        targetClosureDate: initialData.targetClosureDate || initialData.target_closure_date || prevData.targetClosureDate || '',
      };

      setJdDropdownFields(
        buildDropdownFieldsFromValues(
          {
            department: nextData.department,
            demandLevel: nextData.demandLevel,
            clientNoticePeriod: nextData.clientNoticePeriod,
            holdOrClosedReason: nextData.holdOrClosedReason,
            majorSystemExpertise: nextData.majorSystemExpertise,
          },
          {
            department: DEPARTMENT_OPTIONS,
            demandLevel: DEMAND_LEVEL_OPTIONS,
            clientNoticePeriod: CLIENT_NOTICE_PERIOD_OPTIONS,
            holdOrClosedReason: HOLD_CLOSED_REASON_OPTIONS,
            majorSystemExpertise: MAJOR_SYSTEM_OPTIONS,
          }
        )
      );

      return nextData;
      });

      // Parse skills into tags if they exist
      const req = ensureString(initialData.requiredSkills || initialData.required_skills || initialData.required_qualifications);
      const opt = ensureString(initialData.optionalSkills || initialData.optional_skills || initialData.good_to_have_skills);
      const soft = ensureString(initialData.softSkills || initialData.soft_skills);
      if (req) setRequiredSkillTags(req.split(',').map(s => s.trim()).filter(Boolean));
      if (opt) setOptionalSkillTags(opt.split(',').map(s => s.trim()).filter(Boolean));
      if (soft) setSoftSkillTags(soft.split(',').map(s => s.trim()).filter(Boolean));

      dataLoadedRef.current = true;
      setIsAiGenerated(false);
      setCurrentStep(0);
      return;
    }

    if (dataLoadedRef.current) {
      // Debug log removed — was noisy on every effect re-run.
      return;
    }

    const storedData = localStorage.getItem('generatedJdData');
    const stateData = location.state?.prefillData;

    // Debug log removed — was noisy on every effect re-run.

    if (storedData) {
      try {
        const aiData = JSON.parse(storedData);
        // Debug log removed — was leaking AI-generated job description to console.
        
        const ensureString = (value) => {
          if (typeof value === 'string') return value;
          if (Array.isArray(value)) return value.join(', ');
          if (typeof value === 'object' && value !== null) return JSON.stringify(value);
          return String(value || '');
        };

        setFormData(prevData => ({
          ...prevData,
          jobTitle: ensureString(aiData.jobTitle) || prevData.jobTitle,
          jobType: aiData.jobType || prevData.jobType,
          companyLocation: ensureString(aiData.companyLocation) || prevData.companyLocation,
          minExperience: aiData.minExperience || aiData.experienceRange?.split('-')[0]?.trim() || prevData.minExperience,
          maxExperience: aiData.maxExperience || aiData.experienceRange?.split('-')[1]?.trim() || prevData.maxExperience,
          employmentType: aiData.employmentType || prevData.employmentType,
          salaryRange: ensureString(aiData.salaryRange) || prevData.salaryRange,
          jobSummary: ensureString(aiData.jobSummary) || prevData.jobSummary,
          keyResponsibilities: ensureString(aiData.keyResponsibilities) || prevData.keyResponsibilities,
          requiredSkills: ensureString(aiData.requiredSkills) || prevData.requiredSkills,
          optionalSkills: ensureString(aiData.optionalSkills) || prevData.optionalSkills,
          softSkills: ensureString(aiData.softSkills) || prevData.softSkills,
          criticalSkills: Array.isArray(aiData.criticalSkills) ? aiData.criticalSkills : (aiData.criticalSkills ? [aiData.criticalSkills] : []),
          workHours: aiData.workHours || prevData.workHours,
          companyCulture: ensureString(aiData.companyCulture) || prevData.companyCulture,
          travelRequirement: aiData.travelRequirement || prevData.travelRequirement,
          hiringProcess: ensureString(aiData.hiringProcess) || prevData.hiringProcess,
          preferredQualification: ensureString(aiData.preferredQualification) || prevData.preferredQualification,
          preferrenceOne: ensureString(aiData.preferrenceOne) || prevData.preferrenceOne,
          preferrenceTwo: ensureString(aiData.preferrenceTwo) || prevData.preferrenceTwo,
          preferrenceThree: ensureString(aiData.preferrenceThree) || prevData.preferrenceThree
        }));
        
        // Parse skills into tags if they exist
        if (aiData.requiredSkills) {
          const skills = ensureString(aiData.requiredSkills).split(',').map(s => s.trim()).filter(s => s);
          setRequiredSkillTags(skills);
        }
        if (aiData.optionalSkills) {
          const skills = ensureString(aiData.optionalSkills).split(',').map(s => s.trim()).filter(s => s);
          setOptionalSkillTags(skills);
        }
        if (aiData.softSkills) {
          const skills = ensureString(aiData.softSkills).split(',').map(s => s.trim()).filter(s => s);
          setSoftSkillTags(skills);
        }
        
        // Debug log removed — was leaking state-machine info to console.
        localStorage.removeItem('generatedJdData');
        setIsAiGenerated(true);
        dataLoadedRef.current = true;

        // Jump directly to review step since AI has filled all the data
        setCurrentStep(totalSteps - 1);

        // Add a small delay to ensure state is updated
        setTimeout(() => {
          // Debug log removed — was leaking final form data to console.
        }, 100);
      } catch (error) {
        console.error('Error parsing AI-generated data:', error);
      }
    } else if (stateData) {
      // Debug log removed — was leaking state-data (form prefill) to console.

      const ensureString = (value) => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object' && value !== null) return JSON.stringify(value);
        return String(value || '');
      };
      
      setFormData(prevData => ({
        ...prevData,
        jobTitle: ensureString(stateData.jobTitle) || prevData.jobTitle,
        jobType: stateData.jobType || prevData.jobType,
        companyLocation: ensureString(stateData.companyLocation) || prevData.companyLocation,
        minExperience: stateData.minExperience || stateData.experienceRange?.split('-')[0]?.trim() || prevData.minExperience,
        maxExperience: stateData.maxExperience || stateData.experienceRange?.split('-')[1]?.trim() || prevData.maxExperience,
        employmentType: stateData.employmentType || prevData.employmentType,
        salaryRange: ensureString(stateData.salaryRange) || prevData.salaryRange,
        jobSummary: ensureString(stateData.jobSummary) || prevData.jobSummary,
        keyResponsibilities: ensureString(stateData.keyResponsibilities) || prevData.keyResponsibilities,
        requiredSkills: ensureString(stateData.requiredSkills) || prevData.requiredSkills,
        optionalSkills: ensureString(stateData.optionalSkills) || prevData.optionalSkills,
        softSkills: ensureString(stateData.softSkills) || prevData.softSkills,
        criticalSkills: Array.isArray(stateData.criticalSkills) ? stateData.criticalSkills : (stateData.criticalSkills ? [stateData.criticalSkills] : []),
        workHours: stateData.workHours || prevData.workHours,
        companyCulture: ensureString(stateData.companyCulture) || prevData.companyCulture,
        travelRequirement: stateData.travelRequirement || prevData.travelRequirement,
        hiringProcess: ensureString(stateData.hiringProcess) || prevData.hiringProcess,
        preferredQualification: ensureString(stateData.preferredQualification) || prevData.preferredQualification,
        preferrenceOne: ensureString(stateData.preferrenceOne) || prevData.preferrenceOne,
        preferrenceTwo: ensureString(stateData.preferrenceTwo) || prevData.preferrenceTwo,
        preferrenceThree: ensureString(stateData.preferrenceThree) || prevData.preferrenceThree
      }));
      setIsAiGenerated(true);
      dataLoadedRef.current = true;
    }
  }, [initialData]); // initialData enables embedded edit mode

  // Handle form field changes
  const handleChange = (e) => {
    const { id, name, value, type, checked } = e.target;
    const field = id || name;
    let nextValue = type === 'checkbox' ? checked : value;
    if (field === 'openPositions' && nextValue !== '') {
      nextValue = String(Math.max(1, Math.floor(Number(nextValue)) || 1));
    }
    setFormData(prevData => ({
      ...prevData,
      [field]: nextValue,
    }));
  };

  // Handle rich text changes
  const handleRichTextChange = (value, fieldName) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value
    }));
  };

  // Handle skill input change with suggestions
  const handleSkillInputChange = (type, value) => {
    setSkillInput({ ...skillInput, [type]: value });
    
    if (value.length >= 2) {
      const currentSkills = type === 'required' ? requiredSkillTags : 
                           type === 'optional' ? optionalSkillTags : 
                           type === 'soft' ? softSkillTags : formData.criticalSkills;
      const suggestions = getSkillSuggestions(value, currentSkills, type);
      setSkillSuggestions({ ...skillSuggestions, [type]: suggestions });
      setShowSuggestions({ ...showSuggestions, [type]: true });
    } else {
      setShowSuggestions({ ...showSuggestions, [type]: false });
    }
  };

  // Skill tagging functions
  const addSkillTag = (type, skillName = null) => {
    const input = skillName || skillInput[type].trim();
    if (!input) return;

    if (type === 'critical') {
      if (!formData.criticalSkills.includes(input)) {
        setFormData(prev => ({
          ...prev,
          criticalSkills: [...prev.criticalSkills, input]
        }));
      }
    } else {
      let tags, setTags;
      if (type === 'required') {
        tags = requiredSkillTags;
        setTags = setRequiredSkillTags;
      } else if (type === 'optional') {
        tags = optionalSkillTags;
        setTags = setOptionalSkillTags;
      } else {
        tags = softSkillTags;
        setTags = setSoftSkillTags;
      }

      if (!tags.includes(input)) {
        const newTags = [...tags, input];
        setTags(newTags);
        setFormData({
          ...formData,
          [`${type}Skills`]: newTags.join(', ')
        });
      }
    }
    setSkillInput({ ...skillInput, [type]: '' });
    setShowSuggestions({ ...showSuggestions, [type]: false });
  };

  const removeSkillTag = (type, skillToRemove) => {
    if (type === 'critical') {
      setFormData(prev => ({
        ...prev,
        criticalSkills: prev.criticalSkills.filter(skill => skill !== skillToRemove)
      }));
    } else {
      let tags, setTags;
      if (type === 'required') {
        tags = requiredSkillTags;
        setTags = setRequiredSkillTags;
      } else if (type === 'optional') {
        tags = optionalSkillTags;
        setTags = setOptionalSkillTags;
      } else {
        tags = softSkillTags;
        setTags = setSoftSkillTags;
      }

      const newTags = tags.filter(skill => skill !== skillToRemove);
      setTags(newTags);
      setFormData({
        ...formData,
        [`${type}Skills`]: newTags.join(', ')
      });
    }
  };

  const handleSkillInputKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkillTag(type);
    }
  };

  // Handle adding critical skill
  const addCriticalSkill = () => {
    if (newCriticalSkill.trim()) {
      const skillName = newCriticalSkill.trim();
      
      if (!formData.criticalSkills.includes(skillName)) {
        setFormData(prev => ({
          ...prev,
          criticalSkills: [...prev.criticalSkills, skillName]
        }));
        
        setNewCriticalSkill('');
      } else {
        toast.warning('This skill is already added as critical');
      }
    }
  };

  // Handle removing critical skill
  const removeCriticalSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      criticalSkills: prev.criticalSkills.filter(skill => skill !== skillToRemove)
    }));
  };

  // Submit form data to backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only allow submission if explicitly triggered by submit button click
    if (!submitButtonClickedRef.current) {
      // If not on review step, validate all steps and show errors
      if (currentStep !== totalSteps - 1) {
        const allErrors = {};
        for (let i = 0; i < totalSteps - 1; i++) {
          const stepErrors = validateStep(i);
          if (Object.keys(stepErrors).length > 0) {
            allErrors[i] = stepErrors;
          }
        }
        
        if (Object.keys(allErrors).length > 0) {
          setStepErrors(allErrors);
          const firstErrorStep = Math.min(...Object.keys(allErrors).map(Number));
          setCurrentStep(firstErrorStep);
          toast.error('Please complete all required fields before submitting.');
          return;
        }
        
        setCurrentStep(totalSteps - 1);
      }
      submitButtonClickedRef.current = false;
      return;
    }
    
    // Only allow submission from review step
    if (currentStep !== totalSteps - 1) {
      // If not on review step, validate all steps and show errors
      const allErrors = {};
      for (let i = 0; i < totalSteps - 1; i++) {
        const stepErrors = validateStep(i);
        if (Object.keys(stepErrors).length > 0) {
          allErrors[i] = stepErrors;
        }
      }
      
      if (Object.keys(allErrors).length > 0) {
        setStepErrors(allErrors);
        // Jump to first step with errors
        const firstErrorStep = Math.min(...Object.keys(allErrors).map(Number));
        setCurrentStep(firstErrorStep);
        toast.error('Please complete all required fields before submitting.');
        return;
      }
      
      // If all steps are valid, go to review step
      setCurrentStep(totalSteps - 1);
      return;
    }
    
    setLoading(true);

    try {
      const employerEmail = overrideCompanyEmail || localStorage.getItem('employer_email');
      
      if (!employerEmail) {
        console.error('Employer email is missing. Please log in again.');
        toast.error('Session expired. Please log in again.');
        window.location.href = '/hirewithus/login';
        return;
      }

      // Validate required fields
      if (!formData.jobTitle.trim()) {
        toast.error('Job title is required.');
        setLoading(false);
        return;
      }

      if (!formData.jobType) {
        toast.error('Job type is required.');
        setLoading(false);
        return;
      }

      if (!formData.companyLocation.trim()) {
        toast.error('Company location is required.');
        setLoading(false);
        return;
      }

      const minExpNum = Number(formData.minExperience);
      const maxExpNum = Number(formData.maxExperience);

      if (!formData.minExperience || Number.isNaN(minExpNum) || minExpNum < 0) {
        toast.error('Minimum experience is required and must be 0 or greater.');
        setLoading(false);
        return;
      }

      if (
        formData.maxExperience !== '' &&
        !Number.isNaN(maxExpNum) &&
        !Number.isNaN(minExpNum) &&
        maxExpNum < minExpNum
      ) {
        toast.error('Maximum experience must be greater than or equal to minimum experience.');
        setLoading(false);
        return;
      }

      const salaryErrors = validateSalaryRangeInput(formData.salaryRange);
      if (salaryErrors.salaryRange) {
        toast.error(salaryErrors.salaryRange);
        setLoading(false);
        return;
      }

      const toString = (value) => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object' && value !== null) return JSON.stringify(value);
        return String(value || '');
      };

      if (!stripHtml(toString(formData.jobSummary)).trim()) {
        toast.error('Job summary is required.');
        setLoading(false);
        return;
      }

      if (!stripHtml(toString(formData.keyResponsibilities)).trim()) {
        toast.error('Key responsibilities are required.');
        setLoading(false);
        return;
      }

      if (!toString(formData.requiredSkills).trim()) {
        toast.error('Required skills are required.');
        setLoading(false);
        return;
      }

      // Normalize data before submission
      const normalizeForSubmission = (data) => {
        const normalized = { ...data };
        
        const stringFields = [
          'jobTitle', 'companyLocation', 'minExperience', 'maxExperience', 'employmentType',
          'salaryRange', 'preferredQualification', 'jobSummary', 'keyResponsibilities',
          'requiredSkills', 'optionalSkills', 'softSkills', 'workHours', 
          'companyCulture', 'travelRequirement', 'hiringProcess',
          'preferrenceOne', 'preferrenceTwo', 'preferrenceThree'
        ];
        
        stringFields.forEach(field => {
          if (normalized[field] !== undefined) {
            normalized[field] = toString(normalized[field]);
          }
        });
        
        // Handle date field - convert empty string to null for MySQL
        if (normalized.applicationDeadline === '' || !normalized.applicationDeadline) {
          normalized.applicationDeadline = null;
        } else {
          // Ensure it's a valid date string in YYYY-MM-DD format
          normalized.applicationDeadline = normalized.applicationDeadline.trim();
        }

        normalized.salaryRange = normalizeSalaryForSave(normalized.salaryRange);

        // Admin-only Phase 2 / Excel metadata — never send from employer Post Job form.
        const adminOnlyFields = [
          'requirementStatus',
          'department',
          'openPositions',
          'clientNoticePeriod',
          'customerFeedback',
          'demandLevel',
          'candidateCallScript',
          'holdOrClosedReason',
          'spoc',
          'requirementDate',
          'budgetAssignedByClient',
          'majorSystemExpertise',
          'questionsToAsk',
          'targetClosureDate',
          'excelRequirementId',
          'excel_requirement_id',
        ];
        adminOnlyFields.forEach((key) => {
          delete normalized[key];
        });
        
        return normalized;
      };

      const submissionData = normalizeForSubmission({
        ...formData,
      });

      const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email');
      const resolvedJobId =
        explicitJobId ||
        initialData?.job_id ||
        initialData?.jobId ||
        initialData?.id ||
        initialData?.jobID ||
        null;

      let response;
      if (mode === 'edit') {
        if (!resolvedJobId) {
          toast.error('Missing job id. Please refresh and try again.');
          setLoading(false);
          return;
        }
        let url = getApiUrl(`/api/update_jd/${resolvedJobId}`);
        if (employerEmail) {
          url += `?company_email=${encodeURIComponent(employerEmail)}`;
        }
        response = await fetch(url, {
          method: "PUT",
          credentials: 'include',
          headers: { 
            "Content-Type": "application/json",
            'X-Admin-Email': adminEmail || ''
          },
          body: JSON.stringify(submissionData),
        });
      } else {
        let url = getApiUrl(`/api/create_jd`);
        if (employerEmail) {
          url += `?company_email=${encodeURIComponent(employerEmail)}`;
        }
        response = await fetch(url, {
          method: "POST",
          credentials: 'include',
          headers: { 
            "Content-Type": "application/json",
            'X-Admin-Email': adminEmail || ''
          },
          body: JSON.stringify(submissionData),
        });
        if (response.ok) {
          const currentOpenJobs = Number(localStorage.getItem("open_jobs_count")) || 0;
          localStorage.setItem("open_jobs_count", currentOpenJobs + 1);
        }
      }

      if (response.ok) {
        const result = await response.json();
        // clear job cache so new job appears immediately
        if (typeof window !== "undefined") {
          import('../../Hooks/jobHook.js').then(({ clearJobsCacheOnLogout }) => {
            clearJobsCacheOnLogout();
          });
        }

        if (mode === 'edit') {
          // Debug log removed — was leaking server response (job data) to console.
          toast.success('Job description updated.');
          onSuccess?.(result);
          onCancel?.();
        } else {
          // Debug log removed — was leaking server response (job data) to console.
          setPostedJobId(result?.job_id ?? null);
          setShowSuccess(true);

          // Reset form
          setFormData({
            jobTitle: '',
            jobType: '',
            companyLocation: '',
            minExperience: '',
            maxExperience: '',
            employmentType: '',
            salaryRange: '',
            preferredQualification: '',
            jobSummary: '',
            keyResponsibilities: '',
            requiredSkills: '',
            optionalSkills: '',
            softSkills: '',
            criticalSkills: [],
            workHours: '',
            companyCulture: '',
            travelRequirement: '',
            hiringProcess: '',
            applicationDeadline: '',
            mandatoryProfile: false,
            requireVideoResume: false,
            preferrenceOne: '',
            preferrenceTwo: '',
            preferrenceThree: ''
          });
          setRequiredSkillTags([]);
          setOptionalSkillTags([]);
          setSoftSkillTags([]);
          setCurrentStep(0);
          setStepErrors({});
          submitButtonClickedRef.current = false;
          onSuccess?.(result);
        }
      } else {
        const errorData = await response.json();
        console.error(mode === 'edit' ? 'Error updating job:' : 'Error creating job:', errorData);
        console.error('Response status:', response.status);
        console.error('Response headers:', response.headers);
        
        const errorMessage = errorData.error || errorData.message || 'Unknown error occurred';
        if (errorData.reason === 'monthly_limit_reached') {
          // Already a complete, customer-ready sentence — no "Failed…" prefix.
          toast.error(errorMessage, { duration: 12000 });
        } else {
          toast.error(`${mode === 'edit' ? 'Failed to update job' : 'Failed to create job'}: ${errorMessage}`, { duration: 5000 });
        }
      }
    } catch (error) {
      console.error('Network error:', error);
      toast.error(`Network error: ${error.message}\n\nPlease check your connection and try again.`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const closeSuccessPopup = () => {
    setShowSuccess(false);
  };

  return (
    <>
      {/* {isAiGenerated && (
        <div className="ai-generated-notice">
          <div className="ai-notice-content">
            <span className="ai-notice-icon">🤖</span>
            <div className="ai-notice-text">
              <h3>AI-Generated Job Description</h3>
              <p>This form has been pre-filled with AI-generated content. Please review and edit the fields as needed before posting.</p>
            </div>
            <button 
              type="button" 
              className="ai-notice-close"
              onClick={() => setIsAiGenerated(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )} */}

      <div className="flex flex-col w-full gap-6">
        {/* Back Navigation */}
        {!embedded ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/hirewithus/postjob')}
            className="w-fit"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Post Job Options
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (onCancel ? onCancel() : null)}
            className="w-fit"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        )}
        
        {/* Step Indicator */}
        <StepIndicator
          currentStep={currentStep}
          totalSteps={totalSteps}
          steps={steps}
          onStepClick={handleStepClick}
        />

        {/* Main Form */}
        <form 
          className="flex flex-col w-full gap-8 overflow-visible" 
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              // Prevent Enter key from submitting form unless on review step and submit button is focused
              if (e.key === 'Enter' && currentStep === totalSteps - 1) {
                // Only allow Enter if the submit button is focused or if it's explicitly the submit action
                const target = e.target;
                if (target.tagName !== 'BUTTON' || target.type !== 'submit') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              } else if (e.key === 'Enter' && currentStep !== totalSteps - 1) {
                // Prevent Enter from submitting on other steps
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            {/* Step 0: Basic Information */}
            {currentStep === 0 && (
              <Card className="overflow-visible">
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8 overflow-visible">
                <div className="w-full space-y-2">
                  <Label htmlFor="jobTitle" className="text-base">
                    Job Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="jobTitle"
                    placeholder="e.g., Software Engineer, Marketing Manager"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    required
                    className="h-11"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="jobType" className="text-base">
                      Job Type <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.jobType}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, jobType: value }))}
                      required
                    >
                      <SelectTrigger id="jobType" className="h-11">
                        <SelectValue placeholder="Select Job Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Full-time">Full-time</SelectItem>
                        <SelectItem value="Part-time">Part-time</SelectItem>
                        <SelectItem value="Contract">Contract</SelectItem>
                        <SelectItem value="Internship">Internship</SelectItem>
                        <SelectItem value="Remote">Remote</SelectItem>
                        <SelectItem value="Hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyLocation" className="text-base">
                      Location <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="companyLocation"
                      placeholder="e.g., New York, NY or Remote"
                      value={formData.companyLocation}
                      onChange={handleChange}
                      required
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="minExperience" className="text-base">
                      Minimum Experience <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="number"
                      id="minExperience"
                      placeholder="e.g., 2"
                      value={formData.minExperience}
                      onChange={handleChange}
                      min="0"
                      max="50"
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxExperience" className="text-base">Maximum Experience</Label>
                    <Input
                      type="number"
                      id="maxExperience"
                      placeholder="e.g., 5 (leave empty for no limit)"
                      value={formData.maxExperience}
                      onChange={handleChange}
                      min="0"
                      max="50"
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="employmentType" className="text-base">Employment Type</Label>
                    <Select
                      value={formData.employmentType}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, employmentType: value }))}
                    >
                      <SelectTrigger id="employmentType" className="h-11">
                        <SelectValue placeholder="Select Employment Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Permanent">Permanent</SelectItem>
                        <SelectItem value="Contract">Contract</SelectItem>
                        <SelectItem value="Temporary">Temporary</SelectItem>
                        <SelectItem value="Freelance">Freelance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      // Salary can be either a numeric range OR the sentinel
                      // string "Competitive". The rest of the app already
                      // renders freeText salary via formatSalaryDisplay, so
                      // storing "Competitive" here is all we need to make it
                      // show as "Competitive" everywhere it's read.
                      const isCompetitive =
                        (formData.salaryRange || '').trim().toLowerCase() === 'competitive';
                      return (
                        <>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <Label htmlFor="salaryRange" className="text-base">Salary Range</Label>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="competitiveSalary"
                                checked={isCompetitive}
                                onCheckedChange={(checked) => {
                                  setFormData(prev => ({
                                    ...prev,
                                    salaryRange: checked ? 'Competitive' : '',
                                  }));
                                }}
                              />
                              <Label
                                htmlFor="competitiveSalary"
                                className="text-sm font-normal cursor-pointer text-muted-foreground"
                              >
                                Competitive (don't disclose)
                              </Label>
                            </div>
                          </div>
                          <Input
                            type="text"
                            id="salaryRange"
                            placeholder="e.g., ₹3 LPA – ₹5 LPA or 3-5"
                            value={isCompetitive ? '' : formData.salaryRange}
                            onChange={handleChange}
                            className="h-11"
                            disabled={isCompetitive}
                          />
                          <FormDescription className="text-xs mt-1">
                            {isCompetitive
                              ? '💼 Listing will display "Competitive" in place of a range.'
                              : '💰 Including salary increases applications by 30%'}
                          </FormDescription>
                          {stepErrors[0]?.salaryRange && !isCompetitive && (
                            <p className="text-sm text-destructive mt-1">{stepErrors[0].salaryRange}</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="applicationDeadline" className="text-base">Application Deadline</Label>
                  <DatePicker
                    id="applicationDeadline"
                    value={formData.applicationDeadline}
                    onChange={(value) => setFormData(prev => ({ ...prev, applicationDeadline: value }))}
                    placeholder="Select deadline (optional)"
                    minDate={new Date().toISOString().split('T')[0]}
                  />
                </div>
                {/* Step 0 validation errors */}
                {stepErrors[0] && (
                  <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                    <div className="text-sm font-medium text-destructive mb-2">Please fix the following errors:</div>
                    <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                      {Object.values(stepErrors[0]).map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Step 1: Job Details */}
            {currentStep === 1 && (
              <Card className="overflow-visible">
                <CardHeader>
                  <CardTitle>Job Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8 overflow-visible">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="jobSummary" className="text-base">
                      Job Summary <span className="text-destructive">*</span>
                    </Label>
                    <span className={`text-sm font-normal ${getCharCount('jobSummary') > charLimits.jobSummary ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                      {getCharCount('jobSummary')}/{charLimits.jobSummary}
                    </span>
                  </div>
                  <div className="bg-white rounded-md [&_.ql-container]:!overflow-visible [&_.ql-editor]:min-h-[200px]">
                    <ReactQuill
                      theme="snow"
                      value={formData.jobSummary}
                      onChange={(value) => handleRichTextChange(value, 'jobSummary')}
                      modules={quillModules}
                      placeholder="Provide a brief overview of the role and its key responsibilities..."
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="keyResponsibilities" className="text-base">
                      Key Responsibilities <span className="text-destructive">*</span>
                    </Label>
                    <span className={`text-sm font-normal ${getCharCount('keyResponsibilities') > charLimits.keyResponsibilities ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                      {getCharCount('keyResponsibilities')}/{charLimits.keyResponsibilities}
                    </span>
                  </div>
                  <div className="bg-white rounded-md [&_.ql-container]:!overflow-visible [&_.ql-editor]:min-h-[250px]">
                    <ReactQuill
                      theme="snow"
                      value={formData.keyResponsibilities}
                      onChange={(value) => handleRichTextChange(value, 'keyResponsibilities')}
                      modules={quillModules}
                      placeholder="List the main responsibilities and duties for this role..."
                    />
                  </div>
                </div>
                {/* Step 1 validation errors */}
                {stepErrors[1] && (
                  <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                    <div className="text-sm font-medium text-destructive mb-2">Please fix the following errors:</div>
                    <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                      {Object.values(stepErrors[1]).map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Step 2: Skills & Qualifications */}
            {currentStep === 2 && (
              <Card className="overflow-visible">
                <CardHeader>
                  <CardTitle>Skills & Qualifications</CardTitle>
                </CardHeader>
                <CardContent className="space-y-10 overflow-visible">
                {/* Required Skills with Tags */}
                <div className="space-y-4">
                  <Label>
                    Required Skills <span className="text-destructive">*</span> ({requiredSkillTags.length} skills added)
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        placeholder="Type a skill and press Enter (e.g., Python, React, AWS)"
                        value={skillInput.required}
                        onChange={(e) => handleSkillInputChange('required', e.target.value)}
                        onKeyPress={(e) => handleSkillInputKeyPress(e, 'required')}
                        onFocus={() => skillInput.required.length >= 2 && setShowSuggestions({ ...showSuggestions, required: true })}
                        onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, required: false }), 200)}
                      />
                      {showSuggestions.required && skillSuggestions.required.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto">
                          {skillSuggestions.required.map((suggestion, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center px-3 py-2 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0"
                              onClick={() => addSkillTag('required', suggestion)}
                            >
                              <span className="font-medium text-sm">{suggestion}</span>
                              <Badge variant="outline" className="text-xs">
                                {skillsDatabase[suggestion]?.category || 'General'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => addSkillTag('required')}
                      disabled={!skillInput.required.trim()}
                      size="default"
                      style={{
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      }}
                    >
                      + Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">Quick add:</span>
                    {['JavaScript', 'Python', 'React', 'AWS', 'SQL', 'Git', 'Docker', 'Agile'].map(skill => (
                      !requiredSkillTags.includes(skill) && (
                        <Button
                          key={skill}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addSkillTag('required', skill)}
                          className="h-7 text-xs"
                        >
                          + {skill}
                        </Button>
                      )
                    ))}
                  </div>
                  {requiredSkillTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md min-h-[50px]">
                      {requiredSkillTags.map((skill, index) => (
                        <Badge 
                          key={index} 
                          variant="default" 
                          className="flex items-center gap-1 pr-1"
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          <span>{skill}</span>
                          <button
                            type="button"
                            onClick={() => removeSkillTag('required', skill)}
                            className="ml-1 hover:bg-primary/80 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Optional Skills with Tags */}
                <div className="space-y-4">
                  <Label>Optional Skills ({optionalSkillTags.length} skills added)</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        placeholder="Type a skill and press Enter"
                        value={skillInput.optional}
                        onChange={(e) => handleSkillInputChange('optional', e.target.value)}
                        onKeyPress={(e) => handleSkillInputKeyPress(e, 'optional')}
                        onFocus={() => skillInput.optional.length >= 2 && setShowSuggestions({ ...showSuggestions, optional: true })}
                        onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, optional: false }), 200)}
                      />
                      {showSuggestions.optional && skillSuggestions.optional.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto">
                          {skillSuggestions.optional.map((suggestion, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center px-3 py-2 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0"
                              onClick={() => addSkillTag('optional', suggestion)}
                            >
                              <span className="font-medium text-sm">{suggestion}</span>
                              <Badge variant="outline" className="text-xs">
                                {skillsDatabase[suggestion]?.category || 'General'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => addSkillTag('optional')}
                      disabled={!skillInput.optional.trim()}
                      size="default"
                      style={{
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      }}
                    >
                      + Add
                    </Button>
                  </div>
                  {optionalSkillTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md min-h-[50px]">
                      {optionalSkillTags.map((skill, index) => (
                        <Badge 
                          key={index} 
                          variant="default" 
                          className="flex items-center gap-1 pr-1"
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          <span>{skill}</span>
                          <button
                            type="button"
                            onClick={() => removeSkillTag('optional', skill)}
                            className="ml-1 hover:bg-primary/80 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Soft Skills with Tags */}
                <div className="space-y-4">
                  <Label>Soft Skills ({softSkillTags.length} skills added)</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        placeholder="Type a soft skill and press Enter (e.g., Leadership, Communication)"
                        value={skillInput.soft}
                        onChange={(e) => handleSkillInputChange('soft', e.target.value)}
                        onKeyPress={(e) => handleSkillInputKeyPress(e, 'soft')}
                        onFocus={() => skillInput.soft.length >= 2 && setShowSuggestions({ ...showSuggestions, soft: true })}
                        onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, soft: false }), 200)}
                      />
                      {showSuggestions.soft && skillSuggestions.soft.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto">
                          {skillSuggestions.soft.map((suggestion, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center px-3 py-2 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0"
                              onClick={() => addSkillTag('soft', suggestion)}
                            >
                              <span className="font-medium text-sm">{suggestion}</span>
                              <Badge variant="outline" className="text-xs">
                                {skillsDatabase[suggestion]?.category || 'Soft Skills'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => addSkillTag('soft')}
                      disabled={!skillInput.soft.trim()}
                      size="default"
                      style={{
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      }}
                    >
                      + Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">Quick add:</span>
                    {['Leadership', 'Communication', 'Problem Solving', 'Teamwork'].map(skill => (
                      !softSkillTags.includes(skill) && (
                        <Button
                          key={skill}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addSkillTag('soft', skill)}
                          className="h-7 text-xs"
                        >
                          + {skill}
                        </Button>
                      )
                    ))}
                  </div>
                  {softSkillTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md min-h-[50px]">
                      {softSkillTags.map((skill, index) => (
                        <Badge 
                          key={index} 
                          variant="default" 
                          className="flex items-center gap-1 pr-1"
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          <span>{skill}</span>
                          <button
                            type="button"
                            onClick={() => removeSkillTag('soft', skill)}
                            className="ml-1 hover:bg-primary/80 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Critical Skills */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Critical Skills ({formData.criticalSkills.length} skills added)</Label>
                    <FormDescription className="mt-1 text-xs">
                      Essential skills required. Candidates missing these will not be considered.
                    </FormDescription>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        placeholder="Type a critical skill and press Enter (e.g., Python, React, AWS)"
                        value={skillInput.critical}
                        onChange={(e) => handleSkillInputChange('critical', e.target.value)}
                        onKeyPress={(e) => handleSkillInputKeyPress(e, 'critical')}
                        onFocus={() => skillInput.critical.length >= 2 && setShowSuggestions({ ...showSuggestions, critical: true })}
                        onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, critical: false }), 200)}
                      />
                      {showSuggestions.critical && skillSuggestions.critical.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto">
                          {skillSuggestions.critical.map((suggestion, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center px-3 py-2 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0"
                              onClick={() => addSkillTag('critical', suggestion)}
                            >
                              <span className="font-medium text-sm">{suggestion}</span>
                              <Badge variant="outline" className="text-xs">
                                {skillsDatabase[suggestion]?.category || 'Critical'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => addSkillTag('critical')}
                      disabled={!skillInput.critical.trim()}
                      size="default"
                      style={{
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                        borderColor: getColor('primary')
                      }}
                    >
                      + Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">Quick add:</span>
                    {['JavaScript', 'Python', 'React', 'AWS', 'SQL', 'Git', 'Docker', 'Agile'].map(skill => (
                      !formData.criticalSkills.includes(skill) && (
                        <Button
                          key={skill}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addSkillTag('critical', skill)}
                          className="h-7 text-xs"
                        >
                          + {skill}
                        </Button>
                      )
                    ))}
                  </div>
                  {formData.criticalSkills.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md min-h-[50px]">
                      {formData.criticalSkills.map((skill, index) => (
                        <Badge 
                          key={index} 
                          variant="default" 
                          className="flex items-center gap-1 pr-1"
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          <span>{skill}</span>
                          <button
                            type="button"
                            onClick={() => removeSkillTag('critical', skill)}
                            className="ml-1 hover:bg-primary/80 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="preferredQualification" className="text-base">Preferred Qualifications</Label>
                    <span className={`text-sm font-normal ${getCharCount('preferredQualification') > charLimits.preferredQualification ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                      {getCharCount('preferredQualification')}/{charLimits.preferredQualification}
                    </span>
                  </div>
                  <div className="bg-white rounded-md [&_.ql-container]:!overflow-visible [&_.ql-editor]:min-h-[180px]">
                    <ReactQuill
                      theme="snow"
                      value={formData.preferredQualification}
                      onChange={(value) => handleRichTextChange(value, 'preferredQualification')}
                      modules={quillModules}
                      placeholder="List preferred qualifications, certifications, or experience..."
                    />
                  </div>
                </div>
                {/* Step 2 validation errors */}
                {stepErrors[2] && (
                  <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                    <div className="text-sm font-medium text-destructive mb-2">Please fix the following errors:</div>
                    <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                      {Object.values(stepErrors[2]).map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Step 3: Company & Work Details */}
            {currentStep === 3 && (
              <Card className="overflow-visible">
                <CardHeader>
                  <CardTitle>Company & Work Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8 overflow-visible">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="workHours" className="text-base">Work Hours</Label>
                    <Input
                      type="text"
                      id="workHours"
                      placeholder="e.g., 9 AM - 5 PM, Flexible"
                      value={formData.workHours}
                      onChange={handleChange}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="travelRequirement" className="text-base">Travel Requirement</Label>
                    <Select
                      value={formData.travelRequirement}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, travelRequirement: value }))}
                    >
                      <SelectTrigger id="travelRequirement" className="h-11">
                        <SelectValue placeholder="Select Travel Requirement" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No travel required">No travel required</SelectItem>
                        <SelectItem value="Occasional travel">Occasional travel</SelectItem>
                        <SelectItem value="Frequent travel">Frequent travel</SelectItem>
                        <SelectItem value="Extensive travel">Extensive travel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="companyCulture" className="text-base">Company Culture</Label>
                    <span className={`text-sm font-normal ${getCharCount('companyCulture') > charLimits.companyCulture ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                      {getCharCount('companyCulture')}/{charLimits.companyCulture}
                    </span>
                  </div>
                  <div className="bg-white rounded-md [&_.ql-container]:!overflow-visible [&_.ql-editor]:min-h-[200px]">
                    <ReactQuill
                      theme="snow"
                      value={formData.companyCulture}
                      onChange={(value) => handleRichTextChange(value, 'companyCulture')}
                      modules={quillModules}
                      placeholder="Describe your company culture, values, and work environment..."
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="hiringProcess" className="text-base">Hiring Process</Label>
                    <span className={`text-sm font-normal ${getCharCount('hiringProcess') > charLimits.hiringProcess ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                      {getCharCount('hiringProcess')}/{charLimits.hiringProcess}
                    </span>
                  </div>
                  <div className="bg-white rounded-md [&_.ql-container]:!overflow-visible [&_.ql-editor]:min-h-[200px]">
                    <ReactQuill
                      theme="snow"
                      value={formData.hiringProcess}
                      onChange={(value) => handleRichTextChange(value, 'hiringProcess')}
                      modules={quillModules}
                      placeholder="Describe the application and interview process..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Step 4: Preferences */}
            {currentStep === 4 && (
              <Card className="overflow-visible">
                <CardHeader>
                  <CardTitle>Preferences</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8 overflow-visible">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="preferrenceOne" className="text-base">Preference 1</Label>
                    <Input
                      type="text"
                      id="preferrenceOne"
                      placeholder="First preference"
                      value={formData.preferrenceOne}
                      onChange={handleChange}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferrenceTwo" className="text-base">Preference 2</Label>
                    <Input
                      type="text"
                      id="preferrenceTwo"
                      placeholder="Second preference"
                      value={formData.preferrenceTwo}
                      onChange={handleChange}
                      className="h-11"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferrenceThree" className="text-base">Preference 3</Label>
                  <Input
                    type="text"
                    id="preferrenceThree"
                    placeholder="Third preference"
                    value={formData.preferrenceThree}
                    onChange={handleChange}
                    className="h-11"
                  />
                </div>
                <div className="flex items-center space-x-2 pt-4">
                  <Checkbox
                    id="mandatoryProfile"
                    checked={formData.mandatoryProfile}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, mandatoryProfile: checked }))}
                  />
                  <Label htmlFor="mandatoryProfile" className="font-normal cursor-pointer text-base">
                    Mandatory Profile (Require complete profile)
                  </Label>
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="requireVideoResume"
                    checked={formData.requireVideoResume}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requireVideoResume: checked }))}
                  />
                  <Label htmlFor="requireVideoResume" className="font-normal cursor-pointer text-base">
                    Require Video Resume (candidates must record one before applying)
                  </Label>
                </div>

                {/* College Preference (tier filter) */}
                <div className="pt-5 mt-4 border-t">
                  <Label className="text-base font-medium">College Preference</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Prefer or require candidates from premium colleges. Optional — leave on “Any” to ignore.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {[
                      { v: 'any', label: 'Any college' },
                      { v: 'preferred', label: 'Prefer premium' },
                      { v: 'required', label: 'Require premium' },
                    ].map((opt) => {
                      const active = (formData.collegeRequirement?.mode || 'any') === opt.v;
                      return (
                        <Button
                          key={opt.v}
                          type="button"
                          variant={active ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            collegeRequirement: {
                              ...(prev.collegeRequirement || { mode: 'any', tiers: [], schools: [] }),
                              mode: opt.v,
                            },
                          }))}
                        >
                          {opt.label}
                        </Button>
                      );
                    })}
                  </div>

                  {(formData.collegeRequirement?.mode || 'any') !== 'any' && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-x-6 gap-y-2">
                        {[
                          { t: 1, l: 'Tier 1 (IIT, IIM, IISc, BITS, top NIT, DTU, VIT…)' },
                          { t: 2, l: 'Tier 2 (other NIT, IIIT, SRM, Manipal, Thapar…)' },
                        ].map(({ t, l }) => {
                          const checked = (formData.collegeRequirement?.tiers || []).includes(t);
                          return (
                            <div key={t} className="flex items-center space-x-2">
                              <Checkbox
                                id={`college-tier-${t}`}
                                checked={checked}
                                onCheckedChange={() => setFormData(prev => {
                                  const cur = prev.collegeRequirement || { mode: 'any', tiers: [], schools: [] };
                                  const tiers = checked
                                    ? (cur.tiers || []).filter(x => x !== t)
                                    : [...(cur.tiers || []), t];
                                  return { ...prev, collegeRequirement: { ...cur, tiers } };
                                })}
                              />
                              <Label htmlFor={`college-tier-${t}`} className="font-normal cursor-pointer text-sm">
                                {l}
                              </Label>
                            </div>
                          );
                        })}
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground mb-1.5">Or add specific schools:</p>
                        {(formData.collegeRequirement?.schools || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(formData.collegeRequirement?.schools || []).map((s, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-3 py-1 text-sm text-foreground">
                                {s}
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => {
                                    const cur = prev.collegeRequirement || { mode: 'any', tiers: [], schools: [] };
                                    return { ...prev, collegeRequirement: { ...cur, schools: (cur.schools || []).filter((_, i) => i !== idx) } };
                                  })}
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label={`Remove ${s}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add specific school (e.g. IIT Delhi)"
                            value={collegeSchoolInput}
                            onChange={(e) => setCollegeSchoolInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCollegeSchool(); } }}
                            className="h-10"
                          />
                          <Button type="button" variant="outline" size="sm" onClick={addCollegeSchool}>Add</Button>
                        </div>
                      </div>

                      {formData.collegeRequirement?.mode === 'required' && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                          ⚠ “Require premium” filters out candidates who don’t match. College is a weak proxy for ability — consider <strong>“Prefer premium”</strong> (a soft boost) so strong candidates from other colleges aren’t excluded.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Step 5: Review & Submit */}
            {currentStep === 5 && (
              <ReviewStep
                formData={formData}
                requiredSkillTags={requiredSkillTags}
                optionalSkillTags={optionalSkillTags}
                softSkillTags={softSkillTags}
                onEditStep={handleEditStep}
              />
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t">
              <div>
                {currentStep > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={loading}
                  >
                    Previous
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                {currentStep < totalSteps - 1 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={loading}
                    size="lg"
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                      borderColor: getColor('primary')
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="button"
                    disabled={loading}
                    size="lg"
                    onClick={(e) => {
                      // Explicitly handle click to ensure submission only happens on button click
                      e.preventDefault();
                      e.stopPropagation();
                      submitButtonClickedRef.current = true;
                      handleSubmit(e);
                    }}
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                      borderColor: getColor('primary')
                    }}
                  >
                    {loading ? (mode === 'edit' ? 'Saving...' : 'Creating Job...') : (mode === 'edit' ? 'Save Changes' : 'Post Job')}
                  </Button>
                )}
              </div>
            </div>
          </form>

      {/* Legacy Preview Panel - keeping for now but can be removed */}
      {false && showPreview && (
        <div className="job-preview-panel">
              <div className="preview-header">
                <h2>📋 Job Preview</h2>
                <p>This is how candidates will see your job posting</p>
              </div>
              
              <div className="preview-content">
                <div className="preview-job-header">
                  <h1>{formData.jobTitle || 'Job Title'}</h1>
                  <div className="preview-job-meta">
                    <span className="preview-tag">{formData.jobType || 'Job Type'}</span>
                    <span className="preview-tag">{formData.companyLocation || 'Location'}</span>
                    <span className="preview-tag">
                      {formData.minExperience ? 
                        `${formData.minExperience}${formData.maxExperience ? `-${formData.maxExperience}` : '+'} years` : 
                        'Experience'
                      }
                    </span>
                  </div>
                  {(() => {
                    const salaryPreview = formatSalaryDisplay(formData.salaryRange);
                    return salaryPreview !== 'Not specified' ? (
                      <div className="preview-salary">💰 {salaryPreview}</div>
                    ) : null;
                  })()}
                </div>

                {formData.jobSummary && (
                  <div className="preview-section">
                    <h3>About the Role</h3>
                    <div dangerouslySetInnerHTML={{ __html: formData.jobSummary }} />
                  </div>
                )}

                {formData.keyResponsibilities && (
                  <div className="preview-section">
                    <h3>Key Responsibilities</h3>
                    <div dangerouslySetInnerHTML={{ __html: formData.keyResponsibilities }} />
                  </div>
                )}

                {requiredSkillTags.length > 0 && (
                  <div className="preview-section">
                    <h3>Required Skills</h3>
                    <div className="preview-skills">
                      {requiredSkillTags.map((skill, index) => (
                        <span key={index} className="preview-skill-tag required">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {optionalSkillTags.length > 0 && (
                  <div className="preview-section">
                    <h3>Nice to Have</h3>
                    <div className="preview-skills">
                      {optionalSkillTags.map((skill, index) => (
                        <span key={index} className="preview-skill-tag optional">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {softSkillTags.length > 0 && (
                  <div className="preview-section">
                    <h3>Soft Skills</h3>
                    <div className="preview-skills">
                      {softSkillTags.map((skill, index) => (
                        <span key={index} className="preview-skill-tag soft">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {formData.criticalSkills.length > 0 && (
                  <div className="preview-section critical">
                    <h3>⚠️ Critical Skills Required</h3>
                    <div className="preview-skills">
                      {formData.criticalSkills.map((skill, index) => (
                        <span key={index} className="preview-skill-tag critical">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {formData.companyCulture && (
                  <div className="preview-section">
                    <h3>Company Culture</h3>
                    <div dangerouslySetInnerHTML={{ __html: formData.companyCulture }} />
                  </div>
                )}

                {formData.hiringProcess && (
                  <div className="preview-section">
                    <h3>Hiring Process</h3>
                    <div dangerouslySetInnerHTML={{ __html: formData.hiringProcess }} />
                  </div>
                )}

                <div className="preview-apply-section">
                  <button className="preview-apply-btn" disabled>Apply Now</button>
                  <button className="preview-save-btn" disabled>Save Job</button>
                </div>
              </div>
            </div>
          )}
      </div>

      {showSuccess && mode !== 'edit' && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 cursor-pointer" onClick={closeSuccessPopup}></div>
          <div className="relative bg-transparent p-0 rounded-xl max-w-[500px] w-[90%] text-center shadow-lg overflow-hidden">
            <JobPostingSuccess
              onClose={closeSuccessPopup}
              onBroadcast={
                postedJobId
                  ? () => {
                      setShowSuccess(false);
                      setShowBroadcast(true);
                    }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      <BroadcastDialog
        jobId={postedJobId}
        open={showBroadcast && Boolean(postedJobId)}
        onOpenChange={setShowBroadcast}
      />
    </>
  );
};
